import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, Check } from "lucide-react";

/**
 * CollapsibleSection — drop-in upgrade for the visit page's `Section`.
 *
 * Props:
 *   title        — string
 *   testid       — data-testid for the outer card
 *   sectionId    — string key used for localStorage persistence
 *                  (`visit-section-open:{sectionId}` → "1" | "0")
 *   defaultOpen  — initial open state when nothing persisted (default true)
 *   collapsible  — false disables the collapse control entirely
 *   icon         — optional lucide element for the header
 *   filled       — boolean; when true a small green check shows in the header
 *                  so the doctor sees which sections already have data
 *   summary      — short line rendered next to the title when collapsed
 *   right        — optional element rendered right of the title
 *   scrollTargetId — DOM id used by the stepper anchor navigation
 */
export default function CollapsibleSection({
  title, testid, sectionId, defaultOpen = true, collapsible = true,
  icon, filled = false, summary, right, scrollTargetId, badge, autoOpenWhen = false, children,
}) {
  const key = sectionId ? `visit-section-open:${sectionId}` : null;
  const [open, setOpen] = useState(() => {
    if (!collapsible) return true;
    if (!key) return defaultOpen;
    try {
      const v = localStorage.getItem(key);
      if (v === "0") return false;
      if (v === "1") return true;
    } catch { /* ignore */ }
    return defaultOpen;
  });
  useEffect(() => {
    if (!collapsible || !key) return;
    try { localStorage.setItem(key, open ? "1" : "0"); } catch { /* ignore */ }
  }, [open, key, collapsible]);
  // Auto-surface the section the moment its content becomes relevant
  // (e.g. an education kit is detected mid-visit).
  useEffect(() => {
    if (autoOpenWhen && collapsible) setOpen(true);
  }, [autoOpenWhen, collapsible]);

  return (
    <div
      id={scrollTargetId}
      className="bg-white border border-slate-200 rounded-md scroll-mt-24"
      data-testid={testid}
    >
      <div className="px-5 py-3 border-b border-slate-200 section-title flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          disabled={!collapsible}
          onClick={() => collapsible && setOpen((o) => !o)}
          className={`flex items-center gap-2 text-left flex-1 min-w-0 ${collapsible ? "cursor-pointer hover:text-slate-900" : "cursor-default"}`}
          data-testid={testid ? `${testid}-toggle` : undefined}
        >
          {collapsible && (open ? <ChevronDown size={14} className="text-slate-500 shrink-0" /> : <ChevronRight size={14} className="text-slate-500 shrink-0" />)}
          {icon && <span className="text-pink-600 shrink-0">{icon}</span>}
          <span className="min-w-0 [overflow-wrap:anywhere]">{title}</span>
          {filled && (
            <span
              className="ml-1 inline-flex items-center justify-center rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 w-4 h-4"
              title="This section has data"
              data-testid={testid ? `${testid}-filled` : undefined}
            >
              <Check size={9} strokeWidth={3} />
            </span>
          )}
          {badge && (
            <span
              className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-pink-600 text-white animate-pulse shrink-0"
              data-testid={testid ? `${testid}-badge` : undefined}
            >
              {badge}
            </span>
          )}
          {!open && summary && (
            <span className="ml-2 text-[11px] font-normal text-slate-500 truncate">— {summary}</span>
          )}
        </button>
        {right && <div className="flex flex-wrap items-center gap-2 min-w-0 max-w-full">{right}</div>}
      </div>
      {open && <div className="p-5 space-y-3">{children}</div>}
    </div>
  );
}
